/**
 * Sales and fulfilment dashboard.
 *
 * Two questions drive the layout: "how much did we take", and "are we getting
 * orders out on time". Sales alone would hide a kitchen that is drowning, so
 * fulfilment is given equal space and is broken into stages — accept, kitchen,
 * handover — so a regression points at a station rather than at "the kitchen got
 * slower".
 *
 * All aggregation happens in the database (`restaurant_dashboard_range`) using
 * the restaurant's own timezone, so "today" means the merchant's today.
 */

import {
  formatAmountCompact,
  formatDateKey,
  formatDayTime,
  formatDuration,
  formatMoney,
  formatMoneyCompact,
  monthlySales,
  type DashboardRange,
  type DashboardStats,
  type DashboardWindow,
  type MessageKey,
} from '@yummy/shared';
import { fetchDashboard, fetchEtaInsights } from '@yummy/db';
import {
  BarChart,
  Callout,
  DateRangeField,
  CardGrid,
  Divider,
  DonutChart,
  EmptyState,
  ErrorBanner,
  Icon,
  IconControl,
  HourStrip,
  LoadingState,
  PageHeading,
  ProgressBar,
  RankedBars,
  Row,
  Screen,
  Stack,
  type Tone,
  StatTile,
  Text,
  TitledCard,
  useAsync,
  usePersistentState,
  useResponsive,
  useTheme,
  useLocale,
} from '@yummy/ui-rnr';

import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';
import { View } from 'react-native';
import { ArrowLeft, ArrowRight, ChartColumn, RefreshCw } from 'lucide-react-native';

/**
 * How thick every bar on the page is.
 *
 * One number rather than four: the fulfilment stages were 6, the completed bar was
 * the component's 8, best sellers 6 and the learning card's rows 10, which made the
 * same kind of figure look like four different kinds. Ten, because that is the size
 * the learning card's rows already were and the one the page is read at; best
 * sellers draws its own rows at the same ten (`h-2.5` in `RankedBars`).
 */
const BAR_HEIGHT = 10;

/**
 * The windows a merchant reaches for by name, in the order they are offered.
 *
 * Six tabs became this list plus a calendar for everything else, so the list can
 * grow — `last_28_days` arrived with it — without the row of the page it sits in
 * growing too.
 *
 * The entries hold dictionary keys rather than labels, because they render in
 * whichever locale is in force.
 */
const RANGES: Array<{ value: DashboardRange; labelKey: MessageKey }> = [
  { value: 'today', labelKey: 'merchant.dashboard.rangeToday' },
  { value: 'yesterday', labelKey: 'merchant.dashboard.rangeYesterday' },
  { value: 'week', labelKey: 'merchant.dashboard.rangeWeek' },
  { value: 'last_28_days', labelKey: 'merchant.dashboard.rangeLast28Days' },
  { value: 'month', labelKey: 'merchant.dashboard.rangeMonth' },
  { value: 'last_month', labelKey: 'merchant.dashboard.rangeLastMonth' },
  { value: 'year', labelKey: 'merchant.dashboard.rangeYear' },
];

/**
 * The window the page opens on when nothing has been chosen, or when what was
 * chosen can no longer be read.
 *
 * Today, because the dashboard is opened during a shift, and the day being
 * traded is the question it is opened with.
 */
const DEFAULT_WINDOW: DashboardWindow = { kind: 'preset', range: 'today' };

/**
 * Where the chosen window is kept between visits.
 *
 * Per device and per browser, the same answer as the rail's collapsed state and
 * the cook board's mode: the period a screen is read over belongs to that screen —
 * the counter's iPad during service, not the shop and not the person logged in —
 * so a merchant who reloads, or who comes back after lunch, finds the page as
 * they left it rather than back at today.
 */
const WINDOW_KEY = 'yummy.merchant.dashboard.window';

/** An ISO day key, `YYYY-MM-DD`, the only shape a custom window's ends take. */
const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Whether a value read from storage is a window this page can ask the server for.
 *
 * Storage outlives the build that wrote it and can be edited by hand, so a value
 * is only trusted if the field could show it: a preset is accepted only when it
 * is one of the names `RANGES` offers, and a custom window only when both ends
 * are days in order. Anything else falls back to today rather than being sent to
 * the server, where an unknown range would come back as an error the merchant
 * could not clear without clearing site data.
 *
 * @param value Whatever `localStorage` held under `WINDOW_KEY`.
 * @returns Whether it is a window to open on.
 */
function isDashboardWindow(value: unknown): value is DashboardWindow {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { kind?: unknown; range?: unknown; from?: unknown; to?: unknown };

  if (candidate.kind === 'preset') {
    const range = candidate.range;
    return typeof range === 'string' && RANGES.some((option) => option.value === range);
  }

  if (candidate.kind === 'custom') {
    return (
      typeof candidate.from === 'string' &&
      typeof candidate.to === 'string' &&
      DAY_KEY.test(candidate.from) &&
      DAY_KEY.test(candidate.to) &&
      candidate.from <= candidate.to
    );
  }

  return false;
}

/**
 * Renders the sales and fulfilment dashboard for the active restaurant.
 *
 * @returns The dashboard screen.
 */
export function DashboardScreen() {
  const { t, locale } = useLocale();
  const { palette } = useTheme();
  const { width } = useResponsive();
  const { restaurant } = useMerchant();
  // Read synchronously from storage, so the first paint is already the chosen
  // window rather than today flickering into last month under the merchant's eyes.
  const [storedWindow, setWindow] = usePersistentState<DashboardWindow>(
    WINDOW_KEY,
    DEFAULT_WINDOW,
  );
  // Anything else in storage — a window shape from another version, an edited
  // one, a preset this build no longer offers — reads as today.
  const window = isDashboardWindow(storedWindow) ? storedWindow : DEFAULT_WINDOW;

  const restaurantId = restaurant?.id ?? null;

  const stats = useAsync<DashboardStats | null>(
    async () => (restaurantId ? fetchDashboard(supabase, restaurantId, window) : null),
    [restaurantId, window],
  );

  const insights = useAsync(
    async () => (restaurantId ? fetchEtaInsights(supabase, restaurantId) : null),
    [restaurantId],
  );

  if (!restaurantId) {
    return (
      <Screen>
        <EmptyState
          icon={ChartColumn}
          title={t('merchant.onboarding.noRestaurant')}
          message={t('merchant.onboarding.noRestaurantHint')}
        />
      </Screen>
    );
  }

  if (stats.initial) {
    return (
      <Screen>
        <LoadingState message={t('common.loading')} />
      </Screen>
    );
  }

  if (stats.error || !stats.data) {
    return (
      <Screen>
        <ErrorBanner message={stats.error ?? t('common.somethingWentWrong')} onRetry={stats.reload} />
      </Screen>
    );
  }

  const data = stats.data;
  const s = data.summary;
  const currency = data.currency;

  const onTime = data.on_time_rate;

  // The three fulfilment stages are the pipeline by definition, so their sum is
  // the only coherent denominator. Dividing each stage by `avg_total_seconds`
  // would compare averages taken over different populations — a stage that
  // measured more orders than the total did would report over 100%.
  const stageTotal = s.avg_accept_seconds + s.avg_kitchen_seconds + s.avg_handover_seconds;
  const completionRate = s.orders_placed > 0 ? s.orders_completed / s.orders_placed : 0;
  const cancellationRate = s.orders_placed > 0 ? s.orders_cancelled / s.orders_placed : 0;
  // One dish when the card is a fifth of the row, three when it is a third: the
  // list is the same list, but a card 287 points wide cannot hold three names.

  // Six figures, so the count divides six: every line is full at every width and
  // no resize leaves a gap beside the last tile. Four would fit at some widths
  // and leave two-thirds of a row empty; the floor is what a tile needs for its
  // own hint.
  // The top rung is high because a tile has to hold `A$0.00` at its own type
  // size: at six columns below about 1300 the value clips its own currency.
  const statColumns = width >= 1300 ? 6 : width >= 800 ? 3 : 2;
  // Three columns while there is room for a two-thirds card beside a third: the
  // fulfilment sentences need the width, and the chart needs less than half.
  // 1800, not 1480: the one-row rung gives the fulfilment card two columns of
  // five, which is 40% of the content — and its sentences need about 620 points
  // before they stop wrapping. Below that the two-row rung is the better trade,
  // because the fulfilment card then takes two columns of three.
  const cardColumns = width >= 1800 ? 5 : width >= 1120 ? 3 : 1;
  // Sales takes a whole row of its own: a time series is read along its axis, so
  // its width is the information, and squeezing it into a fifth of a row would
  // make the shape of a trading week into a texture. It opens the grid, so its row
  // is the first one and the four cards keep the spans they had: 2+1+1+1 fills the
  // five-column row below it exactly, and 2+1 twice fills the three-column one.
  const cardSpans =
    cardColumns === 5
      ? [5, 2, 1, 1, 1]
      : cardColumns === 3
        ? [3, 2, 1, 2, 1]
        : undefined;

  const slowest = (insights.data?.slowest_items ?? []).slice(0, cardColumns === 5 ? 1 : 3);

  // Sales is drawn in the bucket the window asks for: hours over a single day,
  // days up to a couple of months, months beyond that. A one-day window through
  // the daily series is one bar as wide as the card, which says nothing the Net
  // sales tile does not, and the server already sends the day's hours beside the
  // days; a year of days is 267 slats a few points wide, which is a texture
  // rather than a shape and asks the reader to add twelve months up in their
  // head. The windows a merchant picks by name are one day, a week, 28 days, a
  // month and a year, so those three buckets cover them all; a drawn window falls
  // to whichever of the three it is nearest.
  //
  // The month count is what decides, not the preset: `year` is scoped from the
  // first of January, so in September it is nine months wide, and a window drawn
  // from May to September deserves the same monthly bars.
  const salesBucket: 'hour' | 'day' | 'month' =
    data.series.length <= 1 ? 'hour' : data.series.length > 62 ? 'month' : 'day';
  const sales =
    salesBucket === 'hour'
      ? data.by_hour.map((hour) => ({
          label: String(hour.hour).padStart(2, '0'),
          value: hour.net_sales_cents,
        }))
      : salesBucket === 'month'
        ? monthlySales(data.series).map((month) => ({
            label: formatDateKey(`${month.key}-01`, 'month', locale),
            value: month.cents,
          }))
        : data.series.map((day) => ({
            label: formatDateKey(day.day, 'short', locale),
            value: day.net_sales_cents,
          }));

  return (
    <Screen constrain={false}>
      <Stack gap="xl">
        {/* Directly under the heading is the one fact the field beside it cannot
            carry and that a merchant reading a figure needs: whose clock it is.
            The identifier is its own label — "Australia/Melbourne" reads the same
            in every language — so it is not a translated string with a
            placeholder in it. */}
        <PageHeading
          title={restaurant?.name ?? ''}
          subtitle={data.timezone}
          minWidth={240}
          action={
            <Row gap="sm" align="center" wrap>
              {/* The window and the refresh share the header's trailing edge:
                  both are about the numbers below rather than about the
                  restaurant, and the field is a control a merchant reaches for as
                  often as the refresh. The refresh is the board's own icon
                  control — the same action as there, so it should be the same
                  button. */}
              <DateRangeField
                label={t('merchant.dashboard.period')}
                from={data.from}
                to={data.to}
                presets={RANGES.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
                activePreset={window.kind === 'preset' ? window.range : null}
                onPreset={(value) => setWindow({ kind: 'preset', range: value as DashboardRange })}
                onRange={(range) => setWindow({ kind: 'custom', from: range.from, to: range.to })}
              />
              <IconControl
                label={stats.loading ? t('common.refreshing') : t('common.refresh')}
                icon={RefreshCw}
                onPress={stats.reload}
                disabled={stats.loading}
              />
            </Row>
          }
        />

        {/* ---------------------------------------------------------------- */}
        {/* Headline numbers                                                  */}
        {/* ---------------------------------------------------------------- */}

        <CardGrid columns={statColumns} minWidth={200}>
          <StatTile
            label={t('merchant.dashboard.netSales')}
            value={formatMoneyCompact(s.net_sales_cents, currency)}
            hint={t('merchant.dashboard.netSalesHint', {
              gross: formatMoney(s.gross_sales_cents, currency),
              discount: formatMoney(s.discounts_cents, currency),
            })}
          />
          <StatTile
            label={t('merchant.dashboard.orders')}
            value={String(s.orders_placed)}
            hint={t('merchant.dashboard.ordersHint', {
              completed: s.orders_completed,
              open: s.orders_open,
            })}
          />
          <StatTile
            label={t('merchant.dashboard.averageOrder')}
            value={formatMoney(s.avg_order_cents, currency)}
            hint={t('merchant.dashboard.averageOrderHint', { count: s.orders_completed })}
          />
          <StatTile
            label={t('merchant.dashboard.onTimeRate')}
            value={onTime === null ? t('units.notMeasured') : `${Math.round(onTime * 100)}%`}
            hint={
              onTime === null
                ? t('merchant.dashboard.onTimeNone')
                : t('merchant.dashboard.onTimeHint', {
                    onTime: s.on_time_count,
                    measured: s.measured_count,
                  })
            }
          />
          <StatTile
            label={t('merchant.dashboard.typicalWait')}
            value={formatDuration(s.median_total_seconds, t)}
            hint={t('merchant.dashboard.typicalWaitHint', {
              duration: formatDuration(s.p90_total_seconds, t),
            })}
          />
          <StatTile
            label={t('merchant.dashboard.cancellations')}
            value={`${Math.round(cancellationRate * 100)}%`}
            hint={t('merchant.dashboard.cancellationsHint', {
              cancelled: s.orders_cancelled,
              placed: s.orders_placed,
            })}
          />
        </CardGrid>

        {/* ---------------------------------------------------------------- */}
        {/* Fulfilment efficiency                                             */}
        {/* ---------------------------------------------------------------- */}

        {/* Four cards, three layouts. One row of five columns when there is width
            for all four — the fulfilment card taking two, the rest one each. Two
            rows of three below that: fulfilment beside the chart, then the ranked
            list beside the model rows, the fulfilment and list cards taking two
            columns each because their content is sentences and names. One column
            when there is no room for any of it.

            The fulfilment card gets two columns in both because its hints are the
            page's longest copy: at half a row each ran to two lines with the width
            to spare beside it. */}
        <CardGrid
          columns={cardColumns}
          spans={cardSpans}>
          {/* Sales over time, first because its span is the whole row: it opens the
              grid the way the tiles opened the page, and the four cards below keep
              the spans they already had. Putting it last left it under the fold on
              a laptop, which is a strange place for the period's own money.

              One measure, not two. The card this replaces drew orders as the outer
              bar and revenue as an inner bar on the same axis, and on real numbers
              that axis is revenue's: a day of S$469 beside 40 orders leaves the
              orders bar at eight per cent of the track — a chart that looks like
              revenue with orange grit at the bottom. The count already has two
              homes below (the Orders tile and the hour strip), so the money is what
              this card adds, and one series is one axis with nothing to explain. */}
          <TitledCard
            title={t('merchant.dashboard.sales')}
            // Twenty points of air on every side, rather than the sixteen every
            // other card takes: this panel is one big figure, and the frame around
            // it reads as a frame only when all four margins are the same — the
            // numbers down the side start as far in as the plot stops short.
            panelClassName="p-5"
            description={
              salesBucket === 'hour'
                ? t('merchant.dashboard.salesHourHint', { currency })
                : salesBucket === 'month'
                  ? t('merchant.dashboard.salesMonthHint', { currency })
                  : t('merchant.dashboard.salesHint', { currency })
            }>
            <BarChart
              data={sales}
              // The plot's height is the card's: 390 overall is 260 of plot plus the
              // band above it and the chart's own furniture — the border, the twenty
              // points of panel padding, and the row of labels under the bars.
              height={260}
              labelEvery={salesBucket === 'hour' ? 3 : salesBucket === 'month' ? 1 : undefined}
              tone="brand"
              yAxis
              // The axis is digits and the sentence above it is the unit: a column
              // of "A$0, A$500, A$1k" repeats the currency on every line and puts a
              // symbol on nothing.
              formatValue={formatAmountCompact}
              legend={{ peak: t('merchant.dashboard.salesPeak') }}
            />
          </TitledCard>
          <TitledCard
            title={t('merchant.dashboard.fulfilment')}
            description={t('merchant.dashboard.fulfilmentHint')}>
            <Stack gap="lg">
                <StageRow
                  label={t('merchant.dashboard.stageSitting')}
                  value={s.avg_accept_seconds}
                  total={stageTotal}
                  description={t('merchant.dashboard.stageSittingHint')}
                />
                <StageRow
                  label={t('merchant.dashboard.stageKitchen')}
                  value={s.avg_kitchen_seconds}
                  total={stageTotal}
                  description={t('merchant.dashboard.stageKitchenHint')}
                />
                <StageRow
                  label={t('merchant.dashboard.stageHandover')}
                  value={s.avg_handover_seconds}
                  total={stageTotal}
                  description={t('merchant.dashboard.stageHandoverHint')}
                />

                <Divider />

                <Row spread gap="md">
                  {/* The same ink and weight as the stage labels above: all five
                      rows name something and state it, and two of them being grey
                      made the card read as two cards — the stages, and then some
                      footnotes. */}
                  <Text className="text-base font-semibold">
                    {t('merchant.dashboard.estimateAccuracy')}
                  </Text>
                  <Text className="text-base font-bold">± {formatDuration(s.eta_mae_seconds, t)}</Text>
                </Row>
                <Text className="text-subtle-foreground text-xs">
                  {t('merchant.dashboard.estimateAccuracyHint')}
                </Text>

                <Row spread gap="md">
                  {/* The same ink and weight as the stage labels above: all five
                      rows name something and state it, and two of them being grey
                      made the card read as two cards — the stages, and then some
                      footnotes. */}
                  <Text className="text-base font-semibold">
                    {t('merchant.dashboard.completedRate')}
                  </Text>
                  <Text className="text-base font-bold">{Math.round(completionRate * 100)}%</Text>
                </Row>
                <ProgressBar progress={completionRate} tone="success" height={BAR_HEIGHT} />
            </Stack>
          </TitledCard>

          <TitledCard title={t('merchant.dashboard.whereOrdersCame')}>
            <Stack gap="lg">
                <HourStrip
                  hours={data.by_hour.map((h) => ({ hour: h.hour, value: h.orders_placed }))}
                  // Taller than the strip's own default, because in this card it
                  // sits above a ring: at 72 the bars read as a squashed footnote
                  // to a chart twice their height.
                  height={128}
                />
                {/* No timezone here: the page states it once, under the
                    restaurant's name, and a card about the shape of the trading
                    day does not need to say it again. */}
                <Text className="text-subtle-foreground text-xs">
                  {t('merchant.dashboard.whereOrdersCameHint')}
                </Text>

                <Divider />

                {/* A ring rather than a bar: the bar said "this much of the way
                    along", which is what a progress bar means, when the fact is
                    a split — this many booked, this many walked up. The ring also
                    uses the height this card has because the one beside it is
                    taller, and the hole is a place to put the share a merchant
                    reads first. */}
                <DonutChart
                  centerLabel={
                    s.orders_placed > 0
                      ? `${Math.round((s.orders_scheduled / s.orders_placed) * 100)}%`
                      : '—'
                  }
                  centerHint={t('merchant.dashboard.scheduledShare')}
                  // Two greys rather than a blue and a grey: booked against
                  // walk-up is two kinds of order, not a good one and a bad one,
                  // and an info-blue segment claimed a judgement the split does
                  // not make. The darker grey is the smaller share, which is the
                  // one that has to stay visible at three per cent.
                  segments={[
                    {
                      label: t('merchant.dashboard.scheduled'),
                      value: s.orders_scheduled,
                      color: palette.mutedForeground,
                    },
                    {
                      label: t('merchant.dashboard.walkUp'),
                      value: s.orders_placed - s.orders_scheduled,
                      color: palette.border,
                    },
                  ]}
                />

                <Text className="text-subtle-foreground text-xs">
                  {t('merchant.dashboard.scheduledVsWalkupHint')}
                </Text>
            </Stack>
          </TitledCard>

          <TitledCard
            title={t('merchant.dashboard.bestSellers')}
            description={t('merchant.dashboard.bestSellersHint')}>
            <RankedBars
              items={data.top_items.map((item) => ({
                label: item.name,
                value: item.quantity,
                note: formatMoneyCompact(item.revenue_cents, currency),
              }))}
            />
          </TitledCard>

          <TitledCard
            title={t('merchant.dashboard.etaLearning')}
            description={t('merchant.dashboard.etaLearningHint')}>
            {insights.data ? (
              /* The card's height is partly its neighbour's: on the rung where
                 all four cards share a row, the ring card is the tallest and the
                 grid stretches this one to match, so the panel is handed more
                 height than its own content asks for. The bars are shares of the
                 slowest tenth, which is what makes them comparable — the
                 percentile is the scale, so the longest one is always full.

                 The two capped gaps take a little of that surplus into the
                 rhythm and leave the rest at the foot. Distributing all of it —
                 which is what `justify-between` did — stretched every gap in the
                 column, so the card's own measure moved whenever a neighbour grew,
                 and the spacing read as slack rather than as this card's rhythm.
                 With no surplus to take, a gap is zero-height and the column is
                 exactly the rhythm below.

                 The window is the *model's*, not the page's: the estimator learns
                 from the last ninety days of completed orders whatever period the
                 picker above is showing, and this card does not refetch when that
                 changes. That is why it is the one thing on the page whose numbers
                 ignore the picker, and why its fetch depends on the restaurant
                 alone. */
              <View className="flex-1 gap-4">
                <Stack gap="xs">
                  {/* The one figure in the card at the tiles' scale, so it carries
                      the brand colour the tiles' own figures do not need: the count
                      is what says whether the estimates can be trusted yet. */}
                  <Text className="text-primary text-4xl font-bold tracking-tight">
                    {insights.data.sample_count}
                  </Text>
                  <Text className="text-muted-foreground text-xs">
                    {t('merchant.dashboard.learnedFrom', { days: insights.data.window_days })}
                  </Text>
                </Stack>

                <CappedGap />

                <Stack gap="lg">
                  <PrepBar
                    label={t('merchant.dashboard.medianPrep')}
                    seconds={insights.data.median_prep_seconds}
                    scale={insights.data.p90_prep_seconds}
                  />
                  <PrepBar
                    label={t('merchant.dashboard.averagePrep')}
                    seconds={insights.data.avg_prep_seconds}
                    scale={insights.data.p90_prep_seconds}
                  />
                  <PrepBar
                    label={t('merchant.dashboard.slowestTen')}
                    seconds={insights.data.p90_prep_seconds}
                    scale={insights.data.p90_prep_seconds}
                  />
                </Stack>

                <CappedGap />

                <View className="border-border gap-3 border-t pt-3">
                  {/* Size says how wrong the estimates are; this says which way,
                      which is the half a merchant can act on. Within a minute is
                      its own sentence rather than "0m long", because a shop whose
                      estimates are right should be told so. */}
                  <Row gap="sm" align="center">
                    <Icon
                      as={insights.data.estimate_bias_seconds >= 0 ? ArrowRight : ArrowLeft}
                      size={14}
                      className="text-primary"
                      aria-hidden
                    />
                    <Text className="text-[13px]">
                      {insights.data.estimate_sample_count === 0
                        ? t('merchant.dashboard.noHistory')
                        : Math.abs(insights.data.estimate_bias_seconds) < 60
                          ? t('merchant.dashboard.biasWithin')
                          : insights.data.estimate_bias_seconds > 0
                            ? t('merchant.dashboard.biasShort', {
                                duration: formatDuration(insights.data.estimate_bias_seconds, t),
                              })
                            : t('merchant.dashboard.biasLong', {
                                duration: formatDuration(-insights.data.estimate_bias_seconds, t),
                              })}
                    </Text>
                  </Row>

                  {slowest.length > 0 ? (
                    <Stack gap="sm">
                      <Text className="text-subtle-foreground text-[11px] font-semibold uppercase tracking-[0.06em]">
                        {t('merchant.dashboard.slowestDishes')}
                      </Text>
                      {/* The same row as the three above, drawn by the same
                          component: a dish's median is another duration, and
                          giving it its own thinner, coloured bar only made the
                          two lists look like two kinds of number. */}
                      {slowest.map((dish) => (
                        <PrepBar
                          key={dish.name}
                          label={dish.name}
                          seconds={dish.median_seconds}
                          scale={slowest[0]!.median_seconds}
                          tone="brand"
                        />
                      ))}
                    </Stack>
                  ) : null}
                </View>

                <Text className="text-subtle-foreground text-xs">
                  {insights.data.is_learning
                    ? t('merchant.dashboard.stillLearningHint', {
                        count: insights.data.sample_count,
                      })
                    : insights.data.last_sample_at
                      ? t('merchant.dashboard.lastLearned', {
                          when: formatDayTime(
                            insights.data.last_sample_at,
                            data.timezone,
                            undefined,
                            t,
                          ),
                        })
                      : t('merchant.dashboard.noHistory')}
                </Text>
              </View>
            ) : (
              <Text className="text-muted-foreground text-sm">
                {t('merchant.dashboard.noHistory')}
              </Text>
            )}
          </TitledCard>

        </CardGrid>

        {s.orders_placed === 0 ? (
          <Callout
            title={t('merchant.dashboard.emptyPeriod')}
            message={t('merchant.dashboard.emptyPeriodHint')}
          />
        ) : null}
      </Stack>
    </Screen>
  );
}

/**
 * A capped flexible gap, for a column that can be handed a neighbour's height.
 *
 * A grown gap with a ceiling rather than `justify-between`: it takes up to its cap
 * of any surplus the row gives the card and leaves the rest at the foot, so
 * the card's own rhythm stays where it was drawn instead of moving whenever a
 * neighbour's content changes height. With no surplus to take it is zero-height,
 * and the parent's own gap is the whole of the space.
 *
 * @returns The gap.
 */
function CappedGap() {
  // `-mt-4` cancels the column's own gap on this gap's leading side, so the space
  // between two blocks is the column's gap plus at most the cap. An empty view
  // between two gapped siblings would otherwise be two gaps and this one.
  return <View className="-mt-4 max-h-3 min-h-0 flex-1" />;
}

/**
 * One of the model's own durations, as a bar against the slowest tenth.
 *
 * Neutral rather than toned: the three are the same measure at three points, so
 * the length is the information and a colour would be a judgement nobody made.
 *
 * @param props The label, the duration, and the value the bar is a share of.
 * @returns The row.
 */
function PrepBar({
  label,
  seconds,
  scale,
  tone = 'neutral',
}: {
  label: string;
  seconds: number;
  /** The longest of the rows shown, which is the full width of the track. */
  scale: number;
  /**
   * Neutral for the model's own three durations, brand for the dishes.
   *
   * The three above are measurements of the card's subject; the dish rows are the
   * part of it a merchant acts on, and the colour says which is which without
   * giving them a different shape — the same row, the same track, one difference.
   */
  tone?: Tone;
}) {
  const { t } = useLocale();
  const share = scale > 0 ? Math.min(1, seconds / scale) : 0;

  return (
    <Stack gap="xs">
      <Row spread gap="md" align="baseline">
        <Text className="text-muted-foreground text-[13px]">{label}</Text>
        <Text className="text-base font-bold">{formatDuration(seconds, t)}</Text>
      </Row>
      <ProgressBar progress={share} tone={tone} height={BAR_HEIGHT} />
    </Stack>
  );
}

/**
 * One stage of the fulfilment pipeline, with its share of the average total.
 *
 * @param props Stage label, average seconds, and the total to compare against.
 * @returns The stage row.
 */
function StageRow({
  label,
  value,
  total,
  description,
}: {
  label: string;
  value: number;
  total: number;
  description: string;
}) {
  const { t } = useLocale();
  // Clamped because the three averages are rounded independently in SQL and
  // can sum to a hair under the value they are measuring.
  const share = total > 0 ? Math.min(1, value / total) : 0;

  return (
    <Stack gap="xs">
      <Row spread gap="md" align="baseline">
        <Text className="text-base font-semibold">{label}</Text>
        <Row gap="sm" align="baseline">
          <Text className="text-base font-bold">{formatDuration(value, t)}</Text>
          <Text className="text-subtle-foreground text-xs">
            {total > 0 ? `${Math.round(share * 100)}%` : t('units.notMeasured')}
          </Text>
        </Row>
      </Row>
      {/* Neutral: the three stages are shares of one whole, and a share is not a
          verdict. Colouring them warning, brand and info said sitting unseen was
          a problem, the kitchen was the good one and the counter was something
          else — three judgements where the fact is one division of one average.
          A colour earns its place where two things are genuinely being told
          apart, which is what the ring's two greys do for booked and walk-up. */}
      <ProgressBar progress={share} tone="neutral" height={BAR_HEIGHT} />
      <Text className="text-subtle-foreground text-xs">{description}</Text>
    </Stack>
  );
}
